import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermission } from '../contexts/PermissionContext';
import { getNotifications } from '../services/api';
import { FiHome, FiUsers, FiUserCheck, FiPackage, FiTarget, FiActivity, FiMap, FiShield, FiCalendar, FiBarChart2, FiAward, FiDollarSign, FiFileText, FiTrendingUp, FiSettings, FiLogOut, FiMenu, FiBell, FiChevronLeft, FiKey, FiSliders, FiGlobe, FiShoppingCart, FiRefreshCw, FiClock, FiUserMinus } from 'react-icons/fi';
import iconImg from '../assets/icon.png';
import logoImg from '../assets/logo.png';

const AdminLayout: React.FC = () => {
    const { user, logout } = useAuth();
    const { canAccess } = usePermission();
    const navigate = useNavigate();
    const [collapsed, setCollapsed] = useState(false);
    const [notifCount, setNotifCount] = useState(0);

    useEffect(() => {
        getNotifications({ lu: 0 }).then(res => setNotifCount(res.data.non_lues)).catch(() => {});
        const interval = setInterval(() => {
            getNotifications({ lu: 0 }).then(res => setNotifCount(res.data.non_lues)).catch(() => {});
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    const handleLogout = () => { logout(); navigate('/login'); };

    const allMenuItems = [
        { path: '/admin', module: 'dashboard', icon: <FiHome />, label: 'Dashboard', end: true },
        { path: '/admin/activites', module: 'activites', icon: <FiActivity />, label: 'Activites' },
        { path: '/admin/commandes', module: 'commandes', icon: <FiShoppingCart />, label: 'Commandes' },
        { path: '/admin/commerciaux', module: 'activites', icon: <FiUserCheck />, label: 'Commerciaux' },
        { path: '/admin/clients', module: 'clients', icon: <FiUsers />, label: 'Clients' },
        { path: '/admin/produits', module: 'produits', icon: <FiPackage />, label: 'Produits' },
        { path: '/admin/objectifs', module: 'objectifs', icon: <FiTarget />, label: 'Objectifs' },
        { path: '/admin/planning', module: 'planning', icon: <FiCalendar />, label: 'Planning' },
        { path: '/admin/cartographie', module: 'cartographie', icon: <FiMap />, label: 'Cartographie' },
    ];

    const managementItems = [
        { path: '/admin/bonus', module: 'bonus', icon: <FiDollarSign />, label: 'Bonus & Commissions' },
        { path: '/admin/zones', module: 'cartographie', icon: <FiGlobe />, label: 'Gestion Zones' },
        { path: '/admin/reporting', module: 'reporting', icon: <FiFileText />, label: 'Reporting' },
        { path: '/admin/analytics', module: 'analytics', icon: <FiTrendingUp />, label: 'Analytics IA' },
        { path: '/admin/synchronisation', module: 'dashboard', icon: <FiRefreshCw />, label: 'Synchronisation' },
        { path: '/admin/historique', module: 'dashboard', icon: <FiClock />, label: 'Historique & Audit' },
    ];

    const adminItems = [
        { path: '/admin/users', module: 'utilisateurs', icon: <FiUserCheck />, label: 'Utilisateurs' },
        { path: '/admin/roles', module: 'admin_roles', icon: <FiKey />, label: 'Roles & Permissions' },
        { path: '/admin/settings', module: 'admin_settings', icon: <FiSliders />, label: 'Parametres' },
    ];

    const visibleMenu = allMenuItems.filter(item => canAccess(item.module));
    const visibleManagement = managementItems.filter(item => canAccess(item.module));
    const visibleAdmin = adminItems.filter(item => canAccess(item.module));

    return (
        <div className="admin-layout">
            <aside className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-header">
                    <div className="sidebar-logo">
                        <img src={iconImg} alt="SalesPulse" />
                        {!collapsed && <span>SalesPulse</span>}
                    </div>
                    <button className="sidebar-toggle" onClick={() => setCollapsed(!collapsed)}>
                        {collapsed ? <FiMenu /> : <FiChevronLeft />}
                    </button>
                </div>

                <nav className="sidebar-nav">
                    {visibleMenu.length > 0 && (
                        <div className="nav-section">
                            {!collapsed && <span className="nav-section-title">Principal</span>}
                            {visibleMenu.map(item => (
                                <NavLink key={item.path} to={item.path} end={(item as any).end} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} title={item.label}>
                                    <span className="nav-icon">{item.icon}</span>
                                    {!collapsed && <span className="nav-label">{item.label}</span>}
                                </NavLink>
                            ))}
                        </div>
                    )}

                    {visibleManagement.length > 0 && (
                        <div className="nav-section">
                            {!collapsed && <span className="nav-section-title">Management</span>}
                            {visibleManagement.map(item => (
                                <NavLink key={item.path} to={item.path} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} title={item.label}>
                                    <span className="nav-icon">{item.icon}</span>
                                    {!collapsed && <span className="nav-label">{item.label}</span>}
                                </NavLink>
                            ))}
                        </div>
                    )}

                    {visibleAdmin.length > 0 && (
                        <div className="nav-section">
                            {!collapsed && <span className="nav-section-title">Administration</span>}
                            {visibleAdmin.map(item => (
                                <NavLink key={item.path} to={item.path} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`} title={item.label}>
                                    <span className="nav-icon">{item.icon}</span>
                                    {!collapsed && <span className="nav-label">{item.label}</span>}
                                </NavLink>
                            ))}
                        </div>
                    )}
                </nav>

                <div className="sidebar-footer">
                    <button className="nav-link logout-btn" onClick={handleLogout} title="Deconnexion">
                        <span className="nav-icon"><FiLogOut /></span>
                        {!collapsed && <span className="nav-label">Deconnexion</span>}
                    </button>
                </div>
            </aside>

            <main className="main-content">
                <header className="top-bar">
                    <div className="top-bar-left">
                        <img src={logoImg} alt="SalesPulse" className="topbar-logo" />
                    </div>
                    <div className="top-bar-right">
                        <button className="notif-btn" onClick={() => navigate('/admin/notifications')} title="Notifications">
                            <FiBell />
                            {notifCount > 0 && <span className="notif-badge">{notifCount}</span>}
                        </button>
                        <div className="user-info">
                            <span className="user-name">{user?.prenom} {user?.nom}</span>
                            <span className="user-role">{user?.role}</span>
                        </div>
                    </div>
                </header>

                <div className="content-area">
                    <Outlet />
                </div>
            </main>
        </div>
    );
};

export default AdminLayout;
